/**
 * The reveal replay's step model (GRA-147): a single pure state shape shared
 * by the Back/Forward buttons, the Jump-to-decision/Jump-to-resolution
 * buttons, the scrubber, and the arrow/Home/End keys, so none of those can
 * drift out of sync with each other. Deliberately free of React and of the
 * DOM: `step` counts Forward candles shown (0 is the decision point, the
 * last Context candle; `max` is the fully-revealed round), the same space
 * Chart's `forwardStepOverride` already speaks (GRA-136).
 *
 * `resolutionStep` is the round's actual resolution: a Trend barrier touch or
 * time cap, a Trade's fill candle, or (Next-Candle) the single answer candle.
 * The reveal opens there rather than at `max` (GRA-147's whole point): a Trade
 * that resolved on the first candle shouldn't force the player to look at a
 * fully-drawn future path to find it.
 */
export type ReplayStep = {
  /** Forward candles currently shown: 0 (decision) through `max` (fully revealed). */
  step: number;
  /** Where the round actually resolved, in the same space, clamped into `[0, max]`. */
  resolutionStep: number;
  /** Total Forward candles available (the scrubber's top end). */
  max: number;
};

function clamp(value: number, max: number): number {
  return Math.max(0, Math.min(max, value));
}

/**
 * The initial replay state: `step` starts at the resolution, not at 0 or at
 * `max`, so the reveal opens on the decisive candle (spec, GRA-147). Both
 * inputs are clamped defensively: a caller passing a resolution past the
 * Forward it was computed against (or a negative one) still lands somewhere
 * on the scrubber instead of breaking it.
 */
export function initReplayStep(resolutionStep: number, max: number): ReplayStep {
  const clampedMax = Math.max(0, max);
  const clampedResolution = clamp(resolutionStep, clampedMax);
  return { step: clampedResolution, resolutionStep: clampedResolution, max: clampedMax };
}

/** Step back (`delta` -1) or forward (`delta` 1), clamped to `[0, max]`. */
export function stepReplay(state: ReplayStep, delta: number): ReplayStep {
  return { ...state, step: clamp(state.step + delta, state.max) };
}

/** Jump to the decision point: the last Context candle, always step 0. */
export function jumpToDecision(state: ReplayStep): ReplayStep {
  return { ...state, step: 0 };
}

/** Jump to the resolution candle this state was initialized with. */
export function jumpToResolution(state: ReplayStep): ReplayStep {
  return { ...state, step: state.resolutionStep };
}

/** Move directly to `value` (the scrubber's onChange), clamped to `[0, max]`. */
export function scrubTo(state: ReplayStep, value: number): ReplayStep {
  return { ...state, step: clamp(value, state.max) };
}
